/** Filtro / agrupación del listado de reuniones (puro). */

export function meetingSearchHaystack(row = {}) {
  return [
    row.title,
    row.customerName,
    row.meetingDate,
    row.attendeeNames,
    row.clientContactNames,
    row.status,
  ]
    .map((v) => String(v || "").toLowerCase())
    .join(" ");
}

export function filterMeetingSessions(
  rows = [],
  { query = "", customer = "", status = "" } = {},
) {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  const customerFilter = String(customer || "").trim();
  const statusFilter = String(status || "").trim();

  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (statusFilter && String(row.status || "") !== statusFilter) return false;
    if (customerFilter) {
      const name = String(row.customerName || "").trim();
      if (name !== customerFilter) return false;
    }
    if (q && !meetingSearchHaystack(row).includes(q)) return false;
    return true;
  });
}

export function uniqueMeetingCustomers(rows = []) {
  const set = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const name = String(row.customerName || "").trim();
    if (name) set.add(name);
  }
  return [...set].sort((a, b) =>
    a.localeCompare(b, "es", { sensitivity: "base" }),
  );
}

/** Agrupa por cliente; sin cliente → "". Orden: clientes A–Z, sin cliente al final. */
export function groupMeetingsByCustomer(rows = []) {
  const map = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = String(row.customerName || "").trim();
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  const keys = [...map.keys()].sort((a, b) => {
    if (!a && b) return 1;
    if (a && !b) return -1;
    return a.localeCompare(b, "es", { sensitivity: "base" });
  });
  return keys.map((customerName) => ({
    customerName,
    meetings: map.get(customerName),
  }));
}

/** Clave de cliente del árbol ("" = sin cliente). */
export function meetingTreeCustomerKey(row = {}) {
  return String(row?.customerName || "").trim();
}

/**
 * Une sesiones hidratadas en el listado (por id), sustituyendo las del mismo id.
 */
export function mergeMeetingSessionRows(existing = [], incoming = []) {
  const map = new Map();
  for (const row of Array.isArray(existing) ? existing : []) {
    const id = Number(row?.id);
    if (Number.isFinite(id)) map.set(id, row);
  }
  for (const row of Array.isArray(incoming) ? incoming : []) {
    const id = Number(row?.id);
    if (!Number.isFinite(id)) continue;
    const prev = map.get(id);
    map.set(id, prev ? { ...prev, ...row } : row);
  }
  return [...map.values()].sort(
    (a, b) =>
      new Date(b.startedAt || b.meetingDate || 0).getTime() -
      new Date(a.startedAt || a.meetingDate || 0).getTime(),
  );
}

/**
 * Grupos del árbol lazy: raíces (clientes) + reuniones ya hidratadas.
 * Con búsqueda/filtro activo y filas cargadas, deriva grupos solo de esas filas.
 */
export function buildMeetingTreeGroups(
  treeClients = [],
  rows = [],
  {
    query = "",
    customer = "",
    status = "",
    hydratedKeys = null,
    loadingKeys = null,
  } = {},
) {
  const filtered = filterMeetingSessions(rows, { query, customer, status });
  const byCustomer = groupMeetingsByCustomer(filtered);
  const meetingsMap = new Map(
    byCustomer.map((g) => [g.customerName, g.meetings]),
  );
  const forceFromRows = Boolean(
    String(query || "").trim() ||
      String(customer || "").trim() ||
      String(status || "").trim(),
  );

  if (forceFromRows) {
    return byCustomer.map((g) => ({
      customerName: g.customerName,
      sessionCount: g.meetings.length,
      meetings: g.meetings,
      hydrated: true,
      loading: false,
    }));
  }

  const roots =
    Array.isArray(treeClients) && treeClients.length
      ? [...treeClients].sort((a, b) => {
          const ak = String(a?.customerName || "").trim();
          const bk = String(b?.customerName || "").trim();
          if (!ak && bk) return 1;
          if (ak && !bk) return -1;
          return ak.localeCompare(bk, "es", { sensitivity: "base" });
        })
      : byCustomer.map((g) => ({
          customerName: g.customerName,
          sessionCount: g.meetings.length,
        }));

  const hydrated =
    hydratedKeys instanceof Set
      ? hydratedKeys
      : new Set(Array.isArray(hydratedKeys) ? hydratedKeys : []);
  const loading =
    loadingKeys instanceof Set
      ? loadingKeys
      : new Set(Array.isArray(loadingKeys) ? loadingKeys : []);

  return roots.map((root) => {
    const key = String(root?.customerName || "").trim();
    const meetings = meetingsMap.get(key) || [];
    const isHydrated = hydrated.has(key);
    return {
      customerName: key,
      sessionCount: Number(root?.sessionCount) || meetings.length || 0,
      meetings: isHydrated ? meetings : meetings,
      hydrated: isHydrated,
      loading: loading.has(key),
    };
  });
}

export function meetingPreviewHeadline(row) {
  if (!row) return "";
  const summary = row.summary;
  if (summary && typeof summary === "object") {
    return String(summary.headline || summary.rawSummary || "").trim();
  }
  return String(row.summaryPreview || "").trim();
}

/**
 * Fecha de reunión para listado (ISO YYYY-MM-DD → locale).
 * @param {string|Date|null|undefined} value
 * @param {string} [locale="es-ES"]
 */
export function formatMeetingListDate(value, locale = "es-ES") {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  let date;
  if (m) {
    date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  } else {
    date = new Date(raw);
  }
  if (Number.isNaN(date.getTime())) return raw;
  const loc = String(locale || "es-ES")
    .toLowerCase()
    .startsWith("en")
    ? "en-GB"
    : String(locale || "es-ES")
          .toLowerCase()
          .startsWith("es")
      ? "es-ES"
      : locale || "es-ES";
  return date.toLocaleDateString(loc, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
